/**
 * Coach Engine — Observation Schema
 *
 * Every analyzer in the coach engine produces Observation objects.
 * This is the shared contract so every UI surface (session banner,
 * AI insights modal, daily review, live coach) can consume them.
 *
 * Observations are PURE — they describe findings, not rendering.
 * The narrator layer is responsible for turning them into text.
 *
 * @typedef {Object} Observation
 * @property {string}  id             Stable id: `${analyzerKey}:${scopeId}:${hint}`
 * @property {string}  scope          'session' | 'day' | 'trade' | 'account' | 'global' | 'live'
 * @property {string}  scopeId        ID of the analyzed entity (sessionId, dateStr, tradeId, etc.)
 * @property {string}  severity       'success' | 'info' | 'warning' | 'critical'
 * @property {string}  category       'emotional' | 'risk' | 'execution' | 'setup' | 'discipline' | 'variance' | 'confluence' | 'account' | 'process'
 * @property {string}  title          Short headline (≤ 90 chars). Answers "what did we find?"
 * @property {string}  [detail]       Supporting sentence with numbers (≤ 180 chars). Answers "how bad/good?"
 * @property {string}  [action]       Suggested next step (≤ 120 chars). Answers "so what do I do?"
 * @property {Action[]} [actions]     Optional one-click commands the user can execute against
 *                                    journal state. The `action` text is human-readable advice;
 *                                    `actions` are the structured "Pin to setup", "Add lesson",
 *                                    etc. buttons rendered next to the card. Each action is
 *                                    routed to a parent-registered handler by its `type`.
 *                                    See `makeAction` for the schema.
 * @property {Object}  [evidence]     Structured data used by the analyzer (for debugging + LLM narrator)
 * @property {Object}  [metric]       { label, value, baseline?, unit? } — primary metric worth surfacing
 * @property {number}  confidence     0..1 — how certain the analyzer is this is a real signal
 * @property {number}  priority       0..100 — pre-computed importance for ranking
 * @property {string}  [analyzer]     Key of the analyzer that produced this (for dedup + debugging)
 */

// Severity constants — keep in sync with UI color mapping
export const SEVERITY = Object.freeze({
  SUCCESS:  'success',
  INFO:     'info',
  WARNING:  'warning',
  CRITICAL: 'critical'
});

// Severity → tailwind color classes. UI components import from here so colors
// stay consistent across the app (session banner, insights modal, future Live Coach).
export const SEVERITY_STYLE = Object.freeze({
  success:  { border: 'border-l-green-500',  dot: 'bg-green-500',  text: 'text-green-400',  bg: 'bg-green-500/10'  },
  info:     { border: 'border-l-blue-500',   dot: 'bg-blue-500',   text: 'text-blue-400',   bg: 'bg-blue-500/10'   },
  warning:  { border: 'border-l-amber-500',  dot: 'bg-amber-500',  text: 'text-amber-400',  bg: 'bg-amber-500/10'  },
  critical: { border: 'border-l-red-500',    dot: 'bg-red-500',    text: 'text-red-400',    bg: 'bg-red-500/10'    }
});

// Category constants — drive UI filtering + grouping in the Insights modal (future).
export const CATEGORY = Object.freeze({
  EMOTIONAL:  'emotional',   // tilt, revenge, impulse, hesitation
  RISK:       'risk',        // R-variance, stop moves, size escalation
  EXECUTION:  'execution',   // entries, exits, fills, slippage
  SETUP:      'setup',       // setup mix, setup ecology, ATM choice
  DISCIPLINE: 'discipline',  // adherence to plan, overtrading, skipping rules
  VARIANCE:   'variance',    // process-vs-outcome, regression to mean
  CONFLUENCE: 'confluence',  // confluence patterns, missing/universal signals
  ACCOUNT:    'account',     // cross-account divergence, single-account leaks
  PROCESS:    'process'      // thesis validation, lessons, journal quality
});

// Scope constants
export const SCOPE = Object.freeze({
  SESSION:    'session',
  DAY:        'day',
  TRADE:      'trade',
  ACCOUNT:    'account',
  GLOBAL:     'global',
  LIVE:       'live',
  PROGRESS:   'progress',   // cross-session trend/recurrence/resolution (2B-4)
  EXPERIMENT: 'experiment'  // A/B test grouping across variants (Phase 16)
});

/**
 * Build an Observation object with safe defaults. Use this inside analyzers
 * instead of hand-rolling objects so shape stays consistent.
 *
 * @param {Partial<Observation>} fields
 * @returns {Observation}
 */
export const makeObservation = (fields = {}) => {
  const {
    id = '',
    scope = SCOPE.SESSION,
    scopeId = '',
    severity = SEVERITY.INFO,
    category = CATEGORY.PROCESS,
    title = '',
    detail = '',
    action = '',
    actions = null,
    evidence = null,
    metric = null,
    confidence = 0.7,
    priority = 50,
    analyzer = ''
  } = fields;

  return {
    id: id || `${analyzer || 'unknown'}:${scopeId || 'x'}:${Date.now().toString(36)}`,
    scope, scopeId, severity, category,
    title, detail, action,
    actions: Array.isArray(actions) && actions.length > 0 ? actions : null,
    evidence, metric,
    confidence: Math.max(0, Math.min(1, confidence)),
    priority: Math.max(0, Math.min(100, priority)),
    analyzer
  };
};

/**
 * Action types — the union of one-click commands an observation can offer.
 * Adding a new action: append to this object, then teach the parent handler
 * (TradingJournal's `coachActionDispatcher`) how to execute the new type.
 *
 * Naming convention: verb-noun-target. Keep it stable — these strings are
 * persisted in observation snapshots on session.aiInsight, so renaming
 * one breaks historical observations until users regenerate.
 *
 * Payload schemas (per-type):
 *   PIN_CONFLUENCE_TO_SETUP: { setupName, confluenceIds[], confluenceLabels[] }
 *   ADD_RULE_TO_SETUP:       { setupName, rule }
 *   ADD_LESSON_TO_SESSION:   { sessionId, lesson }
 *   TAG_TRADE_MISTAKE:       { tradeIds[], mistake }
 *   JUMP_TO_TRADE:           { tradeId, date? }
 *   MUTE_OBSERVATION:        { observationId, days }
 */
export const ACTION_TYPE = Object.freeze({
  PIN_CONFLUENCE_TO_SETUP: 'pin-confluence-to-setup',
  ADD_RULE_TO_SETUP:       'add-rule-to-setup',
  ADD_LESSON_TO_SESSION:   'add-lesson-to-session',
  TAG_TRADE_MISTAKE:       'tag-trade-mistake',
  JUMP_TO_TRADE:           'jump-to-trade',
  MUTE_OBSERVATION:        'mute-observation',
});

/**
 * @typedef {Object} Action
 * @property {string} type    One of ACTION_TYPE values
 * @property {string} label   Short button text (≤ 24 chars). What the user reads.
 * @property {Object} payload Type-specific data the handler needs to execute the command.
 *                            For 'pin-confluence-to-setup':
 *                              { setupName: string, confluenceIds: string[], confluenceLabels?: string[] }
 * @property {string} [confirmText]  If set, the renderer shows a confirm dialog with this
 *                                   text before dispatching. Use for destructive or
 *                                   non-trivially-undoable commands.
 * @property {string} [doneLabel]    Optional label shown briefly after successful execution
 *                                   (default: "Done"). Use for nicer UX feedback.
 */

/**
 * Build an Action object with safe defaults.
 *
 * @param {Partial<Action>} fields
 * @returns {Action}
 */
export const makeAction = (fields = {}) => {
  const {
    type = '',
    label = '',
    payload = null,
    confirmText = null,
    doneLabel = null,
  } = fields;
  return { type, label, payload, confirmText, doneLabel };
};
